import { useEffect, useState, useRef, useCallback } from "react";
import { ArrowUp } from "lucide-react";

/**
 * PageShell — reusable UX layout for list/log pages.
 * Provides:
 *   • Sticky page header (title / subtitle / meta / right-side actions) — stays visible while scrolling.
 *   • Optional sticky bottom action bar (Save / Save & New / Cancel etc.).
 *   • Floating "Back to Top" button (appears after ~240px of scroll).
 *   • Compact vertical spacing so more rows fit above the fold.
 *   • Fully responsive (mobile / tablet / desktop).
 *
 * NO business logic, API calls, or state changes happen here.
 *
 * Props:
 *   title      — h1 text (required)
 *   subtitle   — small paragraph under the title
 *   overline   — kicker line above the title (icon + short label ok)
 *   meta       — free JSX rendered next to the title (Reference ID / Status pill / etc.)
 *   actions    — free JSX rendered on the right of the header (buttons / search / etc.)
 *   actionBar  — free JSX rendered inside a sticky bottom bar (Save / Cancel etc.)
 *   testid     — data-testid root
 *   className  — extra classes for the outer wrapper
 */
export default function PageShell({
  title,
  subtitle,
  overline,
  meta,
  actions,
  actionBar,
  children,
  testid = "page",
  className = "",
}) {
  const [showTop, setShowTop] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setShowTop((window.scrollY || document.documentElement.scrollTop || 0) > 240);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    // If a page provides an initial input testid via data-first-input on rootRef,
    // focus it after the smooth-scroll settles.
    setTimeout(() => {
      const first = rootRef.current?.querySelector?.("[data-first-input] input, [data-first-input] textarea, [data-first-input] select");
      first?.focus?.();
    }, 350);
  }, []);

  return (
    <div ref={rootRef} className={`min-h-full ${className}`} data-testid={`${testid}-shell`}>
      {/* Sticky header — sits below AppLayout's topbar (which is sticky top-0, ~40px tall) */}
      <div
        className="sticky top-[42px] z-10 -mx-4 md:-mx-6 px-4 md:px-6 py-3 md:py-4 bg-white/95 backdrop-blur border-b border-border"
        data-testid={`${testid}-header`}
      >
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            {overline && <div className="overline flex items-center gap-2">{overline}</div>}
            <div className="flex items-baseline gap-3 flex-wrap">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-display leading-tight">{title}</h1>
              {meta && <div className="flex items-center gap-2 flex-wrap" data-testid={`${testid}-meta`}>{meta}</div>}
            </div>
            {subtitle && <p className="text-xs sm:text-sm text-muted-foreground mt-1 max-w-2xl">{subtitle}</p>}
          </div>
          {actions && <div className="flex items-center gap-2 flex-wrap" data-testid={`${testid}-actions`}>{actions}</div>}
        </div>
      </div>

      {/* Body — compact spacing so more rows fit above the fold */}
      <div className="p-4 md:p-6 space-y-4">
        {children}
      </div>

      {/* Sticky bottom action bar (opt-in) */}
      {actionBar && (
        <div
          className="sticky bottom-0 z-10 -mx-4 md:-mx-6 px-4 md:px-6 py-3 bg-white/95 backdrop-blur border-t border-border flex items-center justify-end gap-2 flex-wrap"
          data-testid={`${testid}-action-bar`}
        >
          {actionBar}
        </div>
      )}

      {/* Floating Back-to-Top */}
      {showTop && (
        <button
          type="button"
          onClick={scrollTop}
          data-testid={`${testid}-back-to-top`}
          aria-label="Back to top"
          className="fixed bottom-20 lg:bottom-6 right-4 lg:right-6 z-30 h-11 w-11 rounded-full bg-primary text-primary-foreground shadow-lg hover:shadow-xl transition-all flex items-center justify-center"
        >
          <ArrowUp size={18} />
        </button>
      )}
    </div>
  );
}
